import { Button, Container } from "@brightagency/headstart-common";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { WPPost } from "../../../../constants/types";
import { ListingAcf } from "../../../../constants/types/acf";
import { useFreeServiceVisibility, useGlobalLinks } from "../../../../services/app-state";
import { filterListingsByCost, filterListingsByRadius } from "../../../../services/app-state/services";
import { useAssessmentState } from "../../../../services/assessment-state";
import { getAcfPostUrl, useListingSorters } from "../../../../utils";
import { CommunityContext } from "../../../../utils/community-context";
import { useSortingCoords } from "../../../../utils/hooks";
import { ServicesInRegionListings } from "../../../organisms/ServicesInRegionListings";
import { HeroAssessmentResults } from "../../HeroAssessmentResults";
import { OtherSupport } from "../../HomeOtherSupport";
import { useAssessmentResults } from "../services";
import { MoreInformation } from "./MoreInformation";
import { YourAnswers } from "./YourAnswers";
import { Outer, PageContainer, Buttons } from "./styled";

const RegionalServiceRelatedListings: React.FC<{
    listings: WPPost<ListingAcf>[];
    title: string;
    pagename: string;
    region: boolean;
    compact: boolean;
}> = ({ listings, title, pagename, region, compact }) => {
    const [showingFreeService] = useFreeServiceVisibility();
    const sortedListings = useMemo(() => filterListingsByCost(listings, showingFreeService), [listings, showingFreeService]);
    return (
        <ServicesInRegionListings displayRegion={region} title={title} pagename={pagename} description='' items={sortedListings} compact={compact} />
    );
};

export const Results: React.FC = () => {
    const [usingLocation, setUsingLocation] = React.useState(true);
    const { isForSelf, reset } = useAssessmentState();
    const { error, data } = useAssessmentResults();
    const { locationSort, nameSort } = useListingSorters();
    const sorter = usingLocation ? locationSort : nameSort;
    const [, { getGlobalLink }] = useGlobalLinks();
    const savedItemsPage = getGlobalLink("saved_items_page");
    const sortCoords = useSortingCoords();
    const [showingFreeService] = useFreeServiceVisibility();

    const title = isForSelf
        ? "Mental health services you may be eligible for"
        : "Mental health services the person may be eligible for";
        
    const general_service = filterListingsByRadius(data?.general?.sort(sorter) ?? [], sortCoords);
    const sortedListings = useMemo(() => filterListingsByCost(general_service, showingFreeService), [general_service, showingFreeService]);
    const regional_service = data?.regional_service?.sort(sorter) ?? [];
    const digital_service = data?.digital_service?.sort(sorter) ?? [];
    const communities = useMemo(() => data?.communities, [data?.communities]);

    return (
        <>
            <Outer>
                <CommunityContext.Provider value={communities}>
                    <HeroAssessmentResults
                        usingLocation={usingLocation}
                        onToggleUsingLocation={() => setUsingLocation(a => !a)}
                        title={title}
                        subTitle="Services near"
                        listings={sortedListings}
                    />
                    <RegionalServiceRelatedListings listings={regional_service} title='Services available in' pagename='local' region={true} compact={false} />
                    <RegionalServiceRelatedListings listings={digital_service} title='Phone and online services' pagename='local' region={false} compact={true} />
                </CommunityContext.Provider>
            </Outer>
            <PageContainer>
                <MoreInformation moreInformationCards={data?.related ?? []} />
                <Container>
                    {error ? <p>Something went wrong, please try again</p> : null}
                    <YourAnswers />
                    <Buttons>
                        <Button maxWidth={-1} appearance="secondary" onClick={reset}>
                            Start over
                        </Button>
                        <p style={{ marginTop: 32 }}>
                            You can access these results anytime from the{" "}
                            <Link to={getAcfPostUrl(savedItemsPage)}>Your saved resources</Link>{" "}
                            page
                        </p>
                    </Buttons>
                </Container>
            </PageContainer>
            <OtherSupport />
        </>
    );
};
